<template>
  <div>
    <MainHeader />
    <main class="provider-config-wrapp">
      <v-container class="grey lighten-5 mb-6">
        <div class="d-flex align-center mb-3">
          <h2 class="mb-0">
            Payout Partner Configuration
          </h2>
          <v-spacer />
          <v-btn
            color="primary"
            class="mr-2"
            @click="openCreateConfigDialog"
          >
            New Mapping
          </v-btn>
          <v-btn
            color="primary"
            text
            :loading="dahabshiilConfigsLoading"
            @click="fetchDahabshiilBranchConfigs"
          >
            Refresh
          </v-btn>
        </div>
        <VDivider class="mb-6" />

        <v-row style="padding: 0 24px 24px;">
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              value="Dahabshiil"
              label="Provider"
              outlined
              readonly
            />
          </v-col>
          <v-col
            cols="12"
            class="pt-0"
          >
            <p class="mb-4">
              Configure branch id per corridor (destination currency + payout method).
            </p>

            <v-data-table
              :headers="dahabshiilConfigHeaders"
              :items="dahabshiilBranchConfigs"
              :loading="dahabshiilConfigsLoading"
              disable-pagination
              hide-default-footer
              class="elevation-1"
              item-key="id"
            >
              <template v-slot:item.serviceType="{ item }">
                {{ item.serviceType || "ALL" }}
              </template>

              <template v-slot:item.sourceCurrency="{ item }">
                {{ item.sourceCurrency || "ALL" }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  color="primary"
                  small
                  class="mr-2"
                  @click="onOpenDahabshiilConfigEditor(item)"
                >
                  Edit
                </v-btn>
                <v-btn
                  color="error"
                  small
                  outlined
                  @click="openDeleteConfigDialog(item)"
                >
                  Delete
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </main>

    <v-dialog
      v-model="showCreateDahabshiilConfigDialog"
      max-width="760"
    >
      <v-card>
        <v-card-title class="text-h5">
          New Branch Mapping
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="createForm.sourceCurrency"
                :items="sourceCurrencies"
                label="Source Currency"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="createForm.destinationCurrency"
                label="Destination Currency"
                outlined
                clearable
                maxlength="3"
                hint="Example: KES"
                persistent-hint
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-select
                v-model="createForm.serviceType"
                :items="serviceTypeOptions"
                item-text="text"
                item-value="value"
                label="Payout Method"
                outlined
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-select
                v-model="createForm.segment"
                :items="segmentOptions"
                item-text="text"
                item-value="value"
                label="Segment"
                outlined
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="createForm.branchCode"
                label="Branch Id"
                outlined
                clearable
                hint="Example: KMT"
                persistent-hint
              />
            </v-col>
          </v-row>

          <div class="d-flex align-center mb-4">
            <v-btn
              color="primary"
              class="mr-3"
              :loading="createValidateLoading"
              :disabled="!createBranchCodeTrimmed"
              @click="validateCreateBranchCode"
            >
              Validate Branch Id
            </v-btn>
            <v-btn
              color="primary"
              :loading="createLoading"
              :disabled="isCreateDisabled"
              @click="createDahabshiilConfig"
            >
              Create
            </v-btn>
          </div>

          <v-alert
            v-if="createValidationResponse"
            dense
            text
            :type="createValidationResponse.valid ? 'success' : 'error'"
            class="mb-3"
          >
            {{ createValidationResponse.message }}
          </v-alert>

          <v-card
            v-if="createValidationResponse"
            outlined
          >
            <v-card-title class="text-subtitle-1">
              Parsed Validation Response
            </v-card-title>
            <v-card-text>
              <div><b>Branch:</b> {{ createValidationResponse.branchCode || "-" }}</div>
              <div><b>Selling Rate:</b> {{ formattedValidatedValue(createValidationResponse.sellingRate) }}</div>
              <div><b>Buying Rate:</b> {{ formattedValidatedValue(createValidationResponse.buyingRate) }}</div>
              <v-btn
                class="mt-3"
                small
                text
                color="primary"
                :disabled="!createValidationResponse.rawResponse"
                @click="openRawResponseDialog(createValidationResponse)"
              >
                Show Raw Response
              </v-btn>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeCreateConfigDialog"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showDahabshiilConfigDialog"
      max-width="760"
    >
      <v-card>
        <v-card-title class="text-h5">
          Edit Branch Id
        </v-card-title>
        <v-card-text v-if="selectedDahabshiilConfig">
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                :value="selectedDahabshiilConfig.sourceCurrency || 'ALL'"
                label="Source Currency"
                outlined
                readonly
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                :value="selectedDahabshiilConfig.destinationCurrency"
                label="Destination Currency"
                outlined
                readonly
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                :value="selectedDahabshiilConfig.serviceType || 'ALL'"
                label="Payout Method"
                outlined
                readonly
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                :value="selectedDahabshiilConfig.branchCode"
                label="Current Branch Id"
                outlined
                readonly
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="newBranchCode"
                label="New Branch Id"
                outlined
                clearable
              />
            </v-col>
          </v-row>

          <div class="d-flex align-center mb-4">
            <v-btn
              color="primary"
              class="mr-3"
              :loading="dahabshiilValidateLoading"
              :disabled="!newBranchCodeTrimmed"
              @click="validateDahabshiilBranchCode"
            >
              Validate Branch Id
            </v-btn>
            <v-btn
              color="primary"
              :loading="dahabshiilUpdateLoading"
              :disabled="!newBranchCodeTrimmed"
              @click="updateDahabshiilBranchCode"
            >
              Update
            </v-btn>
          </div>

          <v-alert
            v-if="dahabshiilValidationResponse"
            dense
            text
            :type="dahabshiilValidationResponse.valid ? 'success' : 'error'"
            class="mb-3"
          >
            {{ dahabshiilValidationResponse.message }}
          </v-alert>

          <v-card
            v-if="dahabshiilValidationResponse"
            outlined
          >
            <v-card-title class="text-subtitle-1">
              Parsed Validation Response
            </v-card-title>
            <v-card-text>
              <div><b>Branch:</b> {{ dahabshiilValidationResponse.branchCode || "-" }}</div>
              <div><b>Selling Rate:</b> {{ formattedValidatedValue(dahabshiilValidationResponse.sellingRate) }}</div>
              <div><b>Buying Rate:</b> {{ formattedValidatedValue(dahabshiilValidationResponse.buyingRate) }}</div>
              <v-btn
                class="mt-3"
                small
                text
                color="primary"
                :disabled="!dahabshiilValidationResponse.rawResponse"
                @click="openRawResponseDialog(dahabshiilValidationResponse)"
              >
                Show Raw Response
              </v-btn>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDahabshiilConfigDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showDeleteDahabshiilConfigDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          Delete Branch Mapping
        </v-card-title>
        <v-card-text v-if="selectedDahabshiilConfigToDelete">
          <p class="mb-2">
            Are you sure you want to delete this mapping?
          </p>
          <div><b>Destination Currency:</b> {{ selectedDahabshiilConfigToDelete.destinationCurrency }}</div>
          <div><b>Payout Method:</b> {{ selectedDahabshiilConfigToDelete.serviceType || "ALL" }}</div>
          <div><b>Branch Id:</b> {{ selectedDahabshiilConfigToDelete.branchCode }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDeleteDahabshiilConfigDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            @click="deleteDahabshiilConfig"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showRawDahabshiilResponseDialog"
      max-width="900"
    >
      <v-card>
        <v-card-title class="text-h6">
          Raw Dahabshiil Response
        </v-card-title>
        <v-card-text>
          <v-btn
            small
            text
            color="primary"
            class="mb-2"
            :disabled="!hasRawDahabshiilResponse"
            @click="copyRawDahabshiilResponse"
          >
            Copy Raw Response
          </v-btn>
          <pre class="raw-response">{{ rawDahabshiilResponse || "No response" }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showRawDahabshiilResponseDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="pageLoading">
      <VProgressCircular
        indeterminate
        size="64"
      />
    </v-overlay>

    <v-snackbar
      v-model="toastVisible"
      :color="toastColor"
      :timeout="4000"
      top
      right
    >
      {{ toastMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import AdminService from "@/services/AdminService";
import MainHeader from "@/components/MainHeader";
// utils
import { rateCurrencies } from "@/utils/staticAutopricing";

export default {
  name: "ProviderConfiguration",
  components: {
    MainHeader
  },
  data() {
    const currencies = rateCurrencies.map((currency) => ({
      text: currency,
      value: currency
    }));

    return {
      pageLoading: false,
      toastVisible: false,
      toastMessage: "",
      toastColor: "success",
      dahabshiilConfigHeaders: [
        { text: "Source Currency", value: "sourceCurrency" },
        { text: "Destination Currency", value: "destinationCurrency" },
        { text: "Payout Method", value: "serviceType" },
        { text: "Segment", value: "segment" },
        { text: "Branch Id", value: "branchCode" },
        { text: "Actions", value: "actions", sortable: false, width: "200px" }
      ],
      serviceTypeOptions: [
        { text: "ALL", value: null },
        { text: "BNK", value: "BNK" },
        { text: "WLT", value: "WLT" },
        { text: "CSH", value: "CSH" },
        { text: "CRD", value: "CRD" }
      ],
      dahabshiilBranchConfigs: [],
      dahabshiilConfigsLoading: false,
      showCreateDahabshiilConfigDialog: false,
      showDahabshiilConfigDialog: false,
      showDeleteDahabshiilConfigDialog: false,
      showRawDahabshiilResponseDialog: false,
      selectedDahabshiilConfig: null,
      selectedDahabshiilConfigToDelete: null,
      newBranchCode: "",
      createForm: {
        sourceCurrency: null,
        destinationCurrency: "",
        serviceType: null,
        branchCode: "",
        segment: "STANDARD"
      },
      createValidationResponse: null,
      dahabshiilValidateLoading: false,
      createValidateLoading: false,
      dahabshiilUpdateLoading: false,
      createLoading: false,
      deleteLoading: false,
      dahabshiilValidationResponse: null,
      selectedValidationResponse: null,
      sourceCurrencies: [
        { text: "ALL", value: null },
        ...currencies
      ],
      segmentOptions: [
        { text: "Standard", value: "STANDARD" },
        { text: "First Transaction", value: "FIRST_TRANSACTION" }
      ]
    };
  },
  computed: {
    newBranchCodeTrimmed() {
      return (this.newBranchCode || "").trim();
    },
    createDestinationCurrencyTrimmed() {
      return (this.createForm.destinationCurrency || "").trim().toUpperCase();
    },
    createBranchCodeTrimmed() {
      return (this.createForm.branchCode || "").trim();
    },
    isCreateDisabled() {
      return !this.createDestinationCurrencyTrimmed || !this.createBranchCodeTrimmed;
    },
    rawDahabshiilResponse() {
      if (!this.selectedValidationResponse) {
        return "";
      }
      return this.selectedValidationResponse.rawResponse || "";
    },
    hasRawDahabshiilResponse() {
      return Boolean(this.rawDahabshiilResponse);
    }
  },
  created() {
    this.fetchDahabshiilBranchConfigs();
  },
  methods: {
    formattedValidatedValue(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return value;
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },
    async fetchDahabshiilBranchConfigs() {
      this.dahabshiilConfigsLoading = true;
      try {
        const response = await AdminService.getDahabshiilBranchRateConfigs();
        const list = Array.isArray(response && response.data) ? response.data : [];
        this.dahabshiilBranchConfigs = list;
      } catch (error) {
        this.showToast(this.getErrorMessage(error, "Failed to load provider configuration"), "error");
      } finally {
        this.dahabshiilConfigsLoading = false;
      }
    },
    resetCreateConfigDialogState() {
      this.createForm = {
        sourceCurrency: null,
        destinationCurrency: "",
        serviceType: null,
        branchCode: "",
        segment: "STANDARD"
      };
      this.createValidationResponse = null;
      this.selectedValidationResponse = null;
      this.showRawDahabshiilResponseDialog = false;
    },
    openCreateConfigDialog() {
      this.resetCreateConfigDialogState();
      this.showCreateDahabshiilConfigDialog = true;
    },
    closeCreateConfigDialog() {
      this.showCreateDahabshiilConfigDialog = false;
      this.resetCreateConfigDialogState();
    },
    onOpenDahabshiilConfigEditor(item) {
      this.selectedDahabshiilConfig = Object.assign({}, item);
      this.newBranchCode = "";
      this.dahabshiilValidationResponse = null;
      this.selectedValidationResponse = null;
      this.showRawDahabshiilResponseDialog = false;
      this.showDahabshiilConfigDialog = true;
    },
    openDeleteConfigDialog(item) {
      this.selectedDahabshiilConfigToDelete = Object.assign({}, item);
      this.showDeleteDahabshiilConfigDialog = true;
    },
    async validateCreateBranchCode() {
      this.createValidateLoading = true;
      this.createValidationResponse = null;

      try {
        const response = await AdminService.validateDahabshiilBranchCode({
          branchCode: this.createBranchCodeTrimmed
        });
        this.createValidationResponse = response && response.data ? response.data : null;
      } catch (error) {
        this.createValidationResponse = {
          valid: false,
          message: this.getErrorMessage(error, "Validation failed"),
          sellingRate: null,
          buyingRate: null,
          rawResponse: ""
        };
      } finally {
        this.createValidateLoading = false;
      }
    },
    async validateDahabshiilBranchCode() {
      this.dahabshiilValidateLoading = true;
      this.dahabshiilValidationResponse = null;

      try {
        const response = await AdminService.validateDahabshiilBranchCode({
          branchCode: this.newBranchCodeTrimmed
        });
        this.dahabshiilValidationResponse = response && response.data ? response.data : null;
      } catch (error) {
        this.dahabshiilValidationResponse = {
          valid: false,
          message: this.getErrorMessage(error, "Validation failed"),
          sellingRate: null,
          buyingRate: null,
          rawResponse: ""
        };
      } finally {
        this.dahabshiilValidateLoading = false;
      }
    },
    async createDahabshiilConfig() {
      this.createLoading = true;
      this.pageLoading = true;

      try {
        await AdminService.createDahabshiilBranchRateConfig({
          sourceCurrency: this.createForm.sourceCurrency,
          destinationCurrency: this.createDestinationCurrencyTrimmed,
          serviceType: this.createForm.serviceType,
          branchCode: this.createBranchCodeTrimmed,
          segment: this.createForm.segment || "STANDARD"
        });
        this.showToast("Provider configuration created", "success");
        this.closeCreateConfigDialog();
        await this.fetchDahabshiilBranchConfigs();
      } catch (error) {
        this.showToast(this.getErrorMessage(error, "Failed to create provider configuration"), "error");
      } finally {
        this.createLoading = false;
        this.pageLoading = false;
      }
    },
    async updateDahabshiilBranchCode() {
      if (!this.selectedDahabshiilConfig) {
        return;
      }

      this.dahabshiilUpdateLoading = true;
      this.pageLoading = true;
      try {
        await AdminService.updateDahabshiilBranchRateConfig(this.selectedDahabshiilConfig.id, {
          branchCode: this.newBranchCodeTrimmed
        });
        this.showToast("Provider configuration updated", "success");
        this.showDahabshiilConfigDialog = false;
        await this.fetchDahabshiilBranchConfigs();
      } catch (error) {
        this.showToast(this.getErrorMessage(error, "Failed to update provider configuration"), "error");
      } finally {
        this.dahabshiilUpdateLoading = false;
        this.pageLoading = false;
      }
    },
    async deleteDahabshiilConfig() {
      if (!this.selectedDahabshiilConfigToDelete) {
        return;
      }

      this.deleteLoading = true;
      this.pageLoading = true;

      try {
        await AdminService.deleteDahabshiilBranchRateConfig(this.selectedDahabshiilConfigToDelete.id);
        this.showToast("Provider configuration deleted", "success");
        this.showDeleteDahabshiilConfigDialog = false;
        this.selectedDahabshiilConfigToDelete = null;
        await this.fetchDahabshiilBranchConfigs();
      } catch (error) {
        this.showToast(this.getErrorMessage(error, "Failed to delete provider configuration"), "error");
      } finally {
        this.deleteLoading = false;
        this.pageLoading = false;
      }
    },
    openRawResponseDialog(validationResponse) {
      this.selectedValidationResponse = validationResponse || null;
      this.showRawDahabshiilResponseDialog = true;
    },
    async copyRawDahabshiilResponse() {
      if (!this.rawDahabshiilResponse) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.rawDahabshiilResponse);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = this.rawDahabshiilResponse;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.showToast("Raw response copied", "success");
      } catch (e) {
        this.showToast("Failed to copy raw response", "error");
      }
    },
    showToast(message, status) {
      this.toastMessage = message;
      this.toastColor = status === "error" ? "red darken-2" : "green darken-2";
      this.toastVisible = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.provider-config-wrapp {
  max-width: 1200px;
  margin: 50px;
}

.raw-response {
  max-height: 420px;
  overflow: auto;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
